import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core';
import { FieldTypeConfig } from '@ngx-formly/core';
import { AnalyticsAwareFieldType } from '../analytics-aware-field-type';
import { Subscription, distinctUntilChanged } from 'rxjs';
import { IdInputStatusEnum } from '../../../models/irembo-id-input-status.enum';
import { IEnvironment } from '../../../models/environment.model';
import {
  populateFormFields,
  resetFieldsToPopulate,
  updateUrlWithApiGatewayBaseUrl,
  getPopulateReferenceForm,
  subscribeToResetFieldFetchData,
  handleErrorMapping,
} from '../../../../utils/utils/data-fetch-widget-utils';
import { FormArray, FormGroup } from '@angular/forms';
import { FormStateService } from '../../../services/formly/form-state.service';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'irembogov-custom-application-number-input',
  templateUrl: './custom-application-number-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomApplicationNumberInputComponent
  extends AnalyticsAwareFieldType<FieldTypeConfig>
  implements OnDestroy, AfterViewInit
{
  private environment: IEnvironment;
  private subscriptions = new Subscription();
  statusClass: string | undefined;
  compareKeyValue: string | number | boolean | undefined;

  fieldsToPopulate: Record<string, string>[] = [];

  constructor(
    @Inject('environment') environment: IEnvironment,
    private http: HttpClient,
    private formStateService: FormStateService,
    private cd: ChangeDetectorRef
  ) {
    super();
    this.environment = environment;
  }

  ngAfterViewInit(): void {
    if (this.field.props?.['populates']) {
      if (!Array.isArray(this.field.props?.['populates'])) {
        throw new Error('populates should be an array');
      }
      this.fieldsToPopulate = this.field.props?.['populates'] ?? [];
    }

    if (this.fieldsToPopulate.length > 0) {
      this.subscriptions.add(
        this.field.formControl.valueChanges
          .pipe(
            distinctUntilChanged(
              (a, b) => JSON.stringify(a) === JSON.stringify(b)
            )
          )
          .subscribe({
            next: () => {
              if (this.field.formControl.invalid) {
                const formRef = getPopulateReferenceForm(this.field, this.form);
                resetFieldsToPopulate(
                  this.fieldsToPopulate,
                  formRef,
                  this.formStateService
                );
              }
            },
          })
      );
    }

    this.subscriptions.add(
      subscribeToResetFieldFetchData(
        this.field,
        this.formStateService,
        this.fieldsToPopulate,
        getPopulateReferenceForm(this.field, this.form)
      )
    );
  }

  onFetchData(applicationNo: string) {
    let { url } = this.field.props;
    if (!url) {
      throw new Error('url property is required');
    }
    if (this.field.props['useBaseUrl']) {
      url = updateUrlWithApiGatewayBaseUrl(url, this.environment);
    }

    this.statusClass = IdInputStatusEnum.VERIFYING;
    this.subscriptions.add(
      this.http
        .get<Record<string, unknown>>(url, {
          headers: { applicationNumber: applicationNo },
        })
        .subscribe({
          next: (res: Record<string, unknown>) => {
            this.handleFetchSuccess(res);
          },
          error: error => {
            const integrationEndpointCode =
              this.field.props?.['integrationEndpointCode'];
            if (integrationEndpointCode && error?.status === 404) {
              this.fetchFromIntegrationEngine(
                applicationNo,
                integrationEndpointCode
              );
            } else {
              this.handleFetchError(error);
            }
          },
        })
    );
  }

  private fetchFromIntegrationEngine(
    applicationNo: string,
    endpointCode: string
  ): void {
    let integrationUrl = this.field.props?.['integrationUrl'];
    if (!integrationUrl) {
      throw new Error(
        'integrationUrl property is required for integration engine fallback'
      );
    }
    if (this.field.props['useBaseUrl']) {
      integrationUrl = updateUrlWithApiGatewayBaseUrl(
        integrationUrl,
        this.environment
      );
    }

    const payload = this.buildIntegrationPayload(applicationNo);

    const params = {
      endpointCode,
      callerId: uuidv4(),
      payload,
      requester: uuidv4(),
    };

    this.subscriptions.add(
      this.http
        .post<Record<string, unknown>>(integrationUrl, params)
        .subscribe({
          next: (res: Record<string, unknown>) => {
            const data = res['data'] as Record<string, unknown>;
            const response =
              data?.['response'] === undefined ? data : data['response'];
            const parsed =
              typeof response === 'string' ? JSON.parse(response) : response;
            this.handleFetchSuccess({ data: parsed });
          },
          error: error => {
            this.handleFetchError(error);
          },
        })
    );
  }

  private buildIntegrationPayload(inputValue: string): Record<string, unknown> {
    const payloadKey = this.field.props?.['integrationPayloadKey'];
    const payloadKeyMappings = this.field.props?.[
      'integrationPayloadKeyMappings'
    ] as any[] | undefined;

    if (!payloadKeyMappings?.length) {
      return { [payloadKey || 'applicationNumber']: inputValue };
    }

    const payload: Record<string, unknown> = {};
    payloadKeyMappings.forEach((mapping: any) => {
      if (mapping.isStatic) {
        payload[mapping.key] = mapping.staticValue;
      } else if (mapping.sourceField) {
        const form = getPopulateReferenceForm(this.field, this.form);
        const control = form?.get(mapping.sourceField);
        payload[mapping.key] = control?.value;
      } else {
        payload[mapping.key] = inputValue;
      }
    });
    return payload;
  }

  private handleFetchSuccess(res: Record<string, unknown>): void {
    this.formControl.setErrors(null);
    this.statusClass = IdInputStatusEnum.SUCCESS;
    this.emitFetchOutcome('success');

    const data = res['data'] as Record<string, unknown>;
    const { details, ...others } = data;

    const newDataObject = {
      ...(details as Record<string, unknown>),
      ...others,
    };
    this.formStateService.saveFetchedDataKeyInFormState(
      this.field,
      newDataObject
    );

    if (this.fieldsToPopulate.length > 0 && this.field.formControl.valid) {
      const formRef: FormGroup<any> | FormArray<any> = getPopulateReferenceForm(
        this.field,
        this.form
      );
      populateFormFields(
        newDataObject,
        this.field,
        formRef,
        this.formStateService
      );
    } else {
      this.field.options?.detectChanges?.(this.field);
    }

    if (this.field.props?.['compareKey']) {
      const compareKey = this.field.props?.['compareKey'];
      const value = newDataObject[compareKey];

      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        this.compareKeyValue = value;
      } else {
        this.compareKeyValue = undefined;
      }
    }

    if (Object.keys(this.field.formControl.errors ?? {}).length > 0) {
      this.statusClass = IdInputStatusEnum.DANGER;
    }

    this.cd.detectChanges();
  }

  private handleFetchError(error: any): void {
    this.compareKeyValue = undefined;
    const { errors, statusClass } = handleErrorMapping(
      error?.error || error,
      this.field.props['errorMapping'],
      'invalidAppNo',
      this.field,
      this.formControl,
      true
    );

    const errorKey = Object.keys(errors)[0];
    const errorMessage =
      this.field.validation?.messages?.[errorKey] || errors[errorKey]?.message;

    if (this.field?.props?.['showGlobalError'] && errorMessage)
      this.formStateService.addGlobalError(this.field, errorMessage);

    this.statusClass = statusClass || IdInputStatusEnum.DANGER;
    this.formControl.setErrors(errors);
    this.cd.detectChanges();
    this.emitFetchOutcome('failure', error);
  }

  deleteFetchedDataKeyInFormState(): void {
    this.formStateService.deleteFetchedDataKeyInFormState(this.field);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
